import { FC, useEffect } from 'react'
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { MdRefresh, MdSearch, MdSettings } from 'react-icons/md'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ListHCPArtesColunaUseCase } from '../../../Usecase/HCP/Artes/list-hcp-artes-coluna-usecase'
import { FiltroItensPendentesType, useMontagemCartelas } from './montagem-cartelas.hook'
import { MontagemCartelasTable } from '../../../Components/MontagemCartelasHCP/montagem-cartelas-table.component'
import { EmProducaoTable } from '../../../Components/MontagemCartelasHCP/em-producao-table.component'
import { TiragemConfigDialog } from '../../../Components/MontagemCartelasHCP/tiragem-config-dialog.component'

interface IMontagemCartelasProps {
  listHCPArtesColunaUseCase: ListHCPArtesColunaUseCase
}

const MontagemCartelas: FC<IMontagemCartelasProps> = ({ listHCPArtesColunaUseCase }) => {
  const {
    aba, setAba,
    grupos, gruposOS,
    totalSemOS, totalComOS,
    comOSCarregados, comOSHasMore, loadingMore, loadMoreComOS,
    loading, lastUpdated,
    filtroBusca, setFiltroBusca,
    filtroItensPendentes, setFiltroItensPendentes,
    fetchDados,
    tiragemConfig,
    produtosDisponiveis,
    configDialogOpen,
    setConfigDialogOpen,
    salvarConfiguracao,
  } = useMontagemCartelas({ listHCPArtesColunaUseCase })

  const FILTRO_PENDENTES_LABELS: Record<FiltroItensPendentesType, string> = {
    todos: 'Todos',
    completos: 'Pedido completo',
    com_pendencias: 'Com pendências',
  }

  useEffect(() => { fetchDados() }, [])

  const atrasadosCount = gruposOS.filter((g) => g.atrasado).length

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>

      {/* Header sticky */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#f5f7fb', pt: 3, pb: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>

        {/* Título + ações */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Montagem de Cartelas</Typography>
            <Typography variant="body2" color="text.secondary">
              Fluxo 2 — Artes aprovadas agrupadas por produto e OS
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MdSettings size={16} />}
              onClick={() => setConfigDialogOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Configurar tiragens
            </Button>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={loading ? <CircularProgress size={14} /> : <MdRefresh size={16} />}
                onClick={fetchDados}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Atualizar
              </Button>
              {lastUpdated && (
                <Typography variant="caption" color="text.disabled">
                  {format(lastUpdated, 'HH:mm', { locale: ptBR })} · próxima em 5 min
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Abas + busca */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}>
          <Tabs value={aba} onChange={(_, v) => { setAba(v); setFiltroBusca('') }} sx={{ minHeight: 42 }}>
            <Tab
              value="sem_os"
              label={
                <Badge badgeContent={totalSemOS} color="default" max={999} sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}>
                  <Box sx={{ pr: 1.5 }}>Aguardando OS</Box>
                </Badge>
              }
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 42 }}
            />
            <Tab
              value="com_os"
              label={
                <Badge
                  badgeContent={atrasadosCount > 0 ? atrasadosCount : totalComOS}
                  color={atrasadosCount > 0 ? 'error' : 'default'}
                  max={999}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}
                >
                  <Box sx={{ pr: 1.5 }}>Em Produção</Box>
                </Badge>
              }
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 42 }}
            />
          </Tabs>

          <TextField
            size="small"
            placeholder={aba === 'sem_os' ? 'Buscar por produto...' : 'Buscar por OS ou produto...'}
            value={filtroBusca}
            onChange={(e) => setFiltroBusca(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><MdSearch size={18} color="#9e9e9e" /></InputAdornment>
              ),
            }}
            sx={{ minWidth: 280, bgcolor: '#fff', borderRadius: 1, mb: 0.5 }}
          />
        </Box>
      </Box>

      {/* Filtro itens_pendentes */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1.5, pb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>Pedido:</Typography>
        {(['todos', 'completos', 'com_pendencias'] as FiltroItensPendentesType[]).map((v) => (
          <Chip
            key={v}
            label={FILTRO_PENDENTES_LABELS[v]}
            size="small"
            onClick={() => setFiltroItensPendentes(v)}
            sx={{
              fontSize: '0.72rem',
              height: 22,
              cursor: 'pointer',
              fontWeight: filtroItensPendentes === v ? 700 : 400,
              bgcolor: filtroItensPendentes === v
                ? (v === 'completos' ? '#2e7d32' : v === 'com_pendencias' ? '#e65100' : '#1565c0')
                : 'rgba(0,0,0,0.06)',
              color: filtroItensPendentes === v ? '#fff' : 'text.secondary',
              '&:hover': { opacity: 0.85 },
            }}
          />
        ))}
      </Box>

      {/* Tabela */}
      <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', mt: 1 }}>
        {loading && (aba === 'sem_os' ? grupos.length === 0 : gruposOS.length === 0) ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : aba === 'sem_os' ? (
          <MontagemCartelasTable grupos={grupos} loading={loading} />
        ) : (
          <EmProducaoTable
            grupos={gruposOS}
            loading={loading}
            comOSTotal={totalComOS}
            comOSCarregados={comOSCarregados}
            comOSHasMore={comOSHasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMoreComOS}
          />
        )}
      </Box>

      {/* Dialog de configuração */}
      <TiragemConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        produtos={produtosDisponiveis}
        config={tiragemConfig}
        onSave={salvarConfiguracao}
      />
    </Box>
  )
}

export default MontagemCartelas
